import { usePlayerStore, playerSelectors } from "@/lib/player/player.store";
import { ProgressBar } from "./progressBar.component";
import { ArtworkMetadata } from "./artworkMetadata.component";
import { PlayerControls } from "./playerControls.component";
import { SpeedControl } from "./speedControl.component";
import { VolumeControl } from "./volumeControl.component";
import { ChaptersList } from "./chaptersList.component";
import { ShareButton } from "@/features/podcast/components/shareButton.component";
import { Button } from "@/components/ui/button";
import { FileText } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { MediaPlayerInstance } from "@vidstack/react";

interface DesktopPlayerContentProps {
	playerRef: React.RefObject<MediaPlayerInstance | null>;
	vidstackWaiting: boolean;
	offsetForSidebar?: boolean;
	previewLimit?: number | null;
	onPreviewLimitReached?: () => void;
	onOpenTranscript: () => void;
}

export function DesktopPlayerContent({
	playerRef,
	vidstackWaiting,
	offsetForSidebar = false,
	previewLimit = null,
	onPreviewLimitReached,
	onOpenTranscript,
}: DesktopPlayerContentProps) {
	const { t } = useTranslation();

	// Get state from Zustand store
	const isPaused = usePlayerStore((state) => state.isPaused);
	const currentTime = usePlayerStore((state) => state.currentTime);
	const duration = usePlayerStore((state) => state.duration);
	const isMuted = usePlayerStore((state) => state.isMuted);
	const playbackSpeed = usePlayerStore((state) => state.playbackSpeed);
	const chapters = usePlayerStore((state) => state.chapters);
	const activeChapterIndex = usePlayerStore(
		(state) => state.activeChapterIndex,
	);
	const showChapters = usePlayerStore((state) => state.showChapters);
	const formatTime = usePlayerStore((state) => state.formatTime);
	const currentMedia = usePlayerStore((state) => state.currentMedia);
	const podcastChapters = usePlayerStore((state) => state.podcastChapters);
	const podcastId = usePlayerStore((state) => state.podcastId);

	// Derived values
	const currentChapter = playerSelectors.getCurrentChapter(
		usePlayerStore.getState(),
	);

	// Get current chapter data for sharing
	const currentChapterData = podcastChapters[activeChapterIndex];
	const totalDuration = Number.isFinite(duration) ? duration : 0;
	const volumePercent = playerSelectors.getVolumePercentage(
		usePlayerStore.getState(),
	);

	return (
		<div
			className={`pointer-events-none fixed left-1/2 ${offsetForSidebar ? "md:left-[calc(50%+8rem)]" : ""} -translate-x-1/2 bottom-20 md:bottom-6 z-40 w-[calc(95%-1rem)] ${offsetForSidebar ? "md:w-[calc(90%-16rem)]" : "md:w-[90%]"} max-w-[1400px] rounded-full border border-white/10 bg-background/60 shadow-2xl backdrop-blur-2xl`}
		>
			{/* Progress bar with chapter markers and hover preview */}
			<div className="pointer-events-auto flex items-center gap-3 px-6 pt-3 w-full">
				<span className="text-xs text-muted-foreground font-medium tabular-nums min-w-[40px] text-right">
					{formatTime(currentTime)}
				</span>
				<div className="flex-1">
					<ProgressBar
						playerRef={playerRef}
						currentTime={currentTime}
						totalDuration={totalDuration}
						chapters={chapters}
						vidstackWaiting={vidstackWaiting}
						previewLimit={previewLimit}
						onPreviewLimitReached={onPreviewLimitReached}
					/>
				</div>
				<span className="text-xs text-muted-foreground font-medium tabular-nums min-w-[40px]">
					{formatTime(totalDuration)}
				</span>
			</div>

			{/* Main controls row */}
			<div className="pointer-events-auto flex h-17 items-center gap-6 px-8 pb-2">
				{/* Left section: Artwork and metadata */}
				<ArtworkMetadata
					currentChapter={currentChapter}
					activeChapterIndex={activeChapterIndex}
					totalChapters={chapters.length}
					podcastTitle={currentMedia?.subtitle}
				/>

				{/* Center section: Playback controls */}
				<div className="flex flex-1 flex-col items-center justify-center">
					<PlayerControls isPaused={isPaused} />
				</div>

				{/* Right section: Additional controls */}
				<div className="flex w-64 items-center justify-end gap-2">
					{/* Speed control */}
					<SpeedControl currentSpeed={playbackSpeed} />

					{/* Volume control */}
					<VolumeControl isMuted={isMuted} volumePercent={volumePercent} />

					{/* Chapters list */}
					<ChaptersList
						chapters={chapters}
						activeChapterIndex={activeChapterIndex}
						showChapters={showChapters}
						totalDuration={totalDuration}
						formatTime={formatTime}
					/>

					{/* Transcript button */}
					{podcastId && (
						<Button
							variant="ghost"
							size="icon"
							className="h-9 w-9 text-white hover:text-primary hover:bg-white/10 hover:scale-105 active:scale-95 transition-all duration-150 rounded-full"
							onClick={onOpenTranscript}
							aria-label={t("transcript.title")}
						>
							<FileText className="h-5 w-5" />
						</Button>
					)}

					{/* Share chapter or podcast */}
					{(currentChapterData?.id || podcastId) && (
						<ShareButton
							chapterId={currentChapterData?.id}
							chapterTitle={currentChapter?.text || currentChapterData?.title}
							podcastId={podcastId || undefined}
							podcastTitle={currentMedia?.subtitle}
							variant="ghost"
							size="icon"
						/>
					)}
				</div>
			</div>
		</div>
	);
}
